/**
 * Getting a crash off the device.
 *
 * Shared by the web app, the native app and the desktop shell, because a
 * reporter is mostly a list of things that must not happen and writing that
 * list three times means getting it right once.
 *
 * ## The rules, and why each exists
 *
 * **It must never throw.** A reporter that can fail is a second crash on top
 * of the first, and it fires exactly when the app is already broken. Every
 * path here swallows.
 *
 * **It must never recurse.** Reporting uses `fetch`; a failed `fetch` is an
 * error; if that error were reported the app would report its way into a loop
 * that outlives the original bug. `sending` guards it.
 *
 * **It must not block a crash screen from rendering.** The send is fire and
 * forget. The user sees the error boundary immediately and the report goes out
 * behind it.
 *
 * **It must survive the process ending moments later.** A crash report is
 * usually the last thing an app does. `keepalive` asks the browser to finish
 * the request after the page goes away; React Native ignores it harmlessly.
 */
import { buildReport, type Breadcrumb, type ReportContext, type ReportKind } from '@aegis/core';

export interface ReporterOptions {
  /** Absolute or relative — whatever the surface's `ApiHost` produces. */
  readonly url: (path: string) => string;
  /**
   * Everything about the build. Read once at startup, because none of it
   * changes while the app runs.
   */
  readonly context: Omit<ReportContext, 'route'>;
  /**
   * Where the user is, asked at the moment of the crash.
   *
   * A function rather than a value because this is the one context field that
   * moves. Both the web app and the native app construct their reporter before
   * the first navigation, so a route captured at construction would say
   * `/today` for every crash the product ever has — which is worse than saying
   * nothing, because it looks like an answer.
   */
  readonly route?: () => string | null;
  /**
   * Off by default in development.
   *
   * A crash in a dev loop is already on screen with a better stack than this
   * would carry, and filing it puts noise in front of whoever reads the real
   * list tomorrow.
   */
  readonly enabled?: boolean;
  /** Injected so a test does not need a network. */
  readonly fetchImpl?: typeof fetch;
}

/**
 * A ring of recent activity, attached to whatever crashes next.
 *
 * Small on purpose. Breadcrumbs are the likeliest carrier of a credential — a
 * request breadcrumb is a URL — and `buildReport` scrubs them, but the surest
 * way not to leak something is not to hold much of it.
 */
const RING = 25;

export class Reporter {
  private readonly crumbs: Breadcrumb[] = [];
  private sending = false;
  /**
   * Fingerprints already sent this session.
   *
   * A crash loop re-reports the same bug on every relaunch; within one session
   * a render error can re-throw on every re-render. The server also limits per
   * fingerprint, and doing it here as well means the device does not spend
   * radio on a report that will be discarded.
   */
  private readonly sent = new Set<string>();

  constructor(private readonly opts: ReporterOptions) {}

  /**
   * The route, defensively.
   *
   * Whatever answers this runs inside the reporter, at the moment the app is
   * already broken — a navigation container that threw during mount is exactly
   * the case where this is asked and exactly the case where it can throw
   * again. Losing the route is fine; losing the report is not.
   */
  private currentRoute(): string | null {
    try {
      return this.opts.route?.() ?? null;
    } catch {
      return null;
    }
  }

  /** Record something that happened. Never throws, never awaits. */
  breadcrumb(category: string, message: string): void {
    try {
      this.crumbs.push({ at: new Date().toISOString(), category, message });
      if (this.crumbs.length > RING) this.crumbs.shift();
    } catch { /* a breadcrumb is never worth an exception */ }
  }

  /**
   * Report one error. Fire and forget by design — the returned promise is for
   * tests, and nothing in the app awaits it.
   *
   * `over` exists for reports that are not about *now* and not about *this*
   * build: the desktop shell hands its webview a panic recorded by a previous
   * launch, possibly of a previous version. Everything else leaves it out.
   */
  async report(
    thrown: unknown,
    kind: ReportKind,
    over?: { readonly occurredAt?: Date; readonly context?: Partial<ReportContext> },
  ): Promise<void> {
    if (this.opts.enabled === false) return;
    // The recursion guard. Without it, a failing `fetch` inside `report`
    // becomes an error, which becomes a report, which fails.
    if (this.sending) return;

    try {
      const report = buildReport(thrown, {
        kind,
        context: { ...this.opts.context, route: this.currentRoute(), ...over?.context },
        breadcrumbs: this.crumbs,
        ...(over?.occurredAt ? { now: over.occurredAt } : {}),
      });

      if (this.sent.has(report.fingerprint)) return;
      this.sent.add(report.fingerprint);

      this.sending = true;
      const send = this.opts.fetchImpl ?? globalThis.fetch;
      await send(this.opts.url('/v1/telemetry/errors'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(report),
        // Deliberately no credentials and no auth header. The endpoint accepts
        // anonymous reports, the server attaches the account when a session
        // happens to be present, and a reporter that needed a token could not
        // report the crash that stopped one being obtained.
        keepalive: true,
      });
    } catch {
      // A crash report that cannot be delivered is not worth a second crash.
      // There is no retry: the next launch will re-report if it still happens.
    } finally {
      this.sending = false;
    }
  }

  /** Test seam — what would be sent, without sending it. */
  pending(): readonly Breadcrumb[] {
    return [...this.crumbs];
  }
}
